import { useContext, useRef, useMemo, useCallback, FC, useEffect } from 'react';
import { Form, FormApi, useFormState } from 'informed';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  clearBonusCode,
  DepositBonusCode,
  setBonusCode,
  useApi,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';
import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { required } from 'helpers/validators';
import { Spinner } from 'components/Spinner';

import { ScInputPromo } from './styled';
import { formatTextUppercase } from 'helpers/common';
import { AddonsContext } from 'contexts/addons';
import { useTheme } from 'hooks/theme';
import { useUserCanIssueBonuses } from 'hooks/bonuses';

interface InputPromoProps {
  pending: boolean;
  pendingUpdateBonus: boolean;
}

export const InputPromo: FC<InputPromoProps> = ({
  pending,
  pendingUpdateBonus,
}) => {
  const { t } = useTranslation();
  const { sendEventToAnalytics } = useAnalytics();
  const { user, fetchUser, pendingUser } = useContext(AuthContext);
  const { bonuses } = useContext(AddonsContext);
  const { presentIcon: PresentIcon } = useTheme();

  const cashierBonuses = useMemo(() => {
    return bonuses.filter(({ cashier }) => cashier);
  }, [bonuses]);

  const isUserCanIssueBonuses = useUserCanIssueBonuses();

  const formApiRef = useRef<FormApi<DepositBonusCode>>();

  const [requestsSetBonusCode, bonusCodeState] = useApi(setBonusCode);

  const [requestClearBonusCode, bonusCodeRemovalState] = useApi(clearBonusCode);

  const handleSubmitCode = useCallback(
    async (values) => {
      const formatCode = formatTextUppercase(values.depositBonusCode);

      const { error } = await requestsSetBonusCode({
        depositBonusCode: formatCode,
      }).request;

      if (error != null || formApiRef.current == null) return;

      formApiRef.current.setValue('depositBonusCode', formatCode);
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__ADD,
        attributes: {
          additionalData: JSON.stringify({
            depositBonusCode: formatCode,
          }),
        },
      });
      fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
    },
    [requestsSetBonusCode, fetchUser, sendEventToAnalytics]
  );

  const handleRemoveCode = useCallback(async () => {
    const { error } = await requestClearBonusCode().request;

    if (error != null) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__CANCEL,
    });
    fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
  }, [requestClearBonusCode, fetchUser, sendEventToAnalytics]);

  const activeOffer = useMemo(() => {
    if (!user?.depositBonusCode) return;

    const offer = cashierBonuses.find(({ code }) => {
      return code === user.depositBonusCode;
    })?.offer;

    return offer ? (
      <p className="info-message">
        <PresentIcon />
        {offer}
      </p>
    ) : null;
  }, [cashierBonuses, PresentIcon, user?.depositBonusCode]);

  return (
    <ScInputPromo>
      {pendingUpdateBonus || pendingUser || pending ? (
        <Spinner size={64} center />
      ) : (
        <>
          {isUserCanIssueBonuses && (
            <Form<DepositBonusCode>
              getApi={(api) => void (formApiRef.current = api)}
              onSubmit={handleSubmitCode}
            >
              <Analytics />
              <FormGroup>
                <div className="form-group__row">
                  <div className="form-group__input">
                    <Input
                      field="depositBonusCode"
                      validate={required}
                      initialValue={user?.depositBonusCode || undefined}
                      disabled={user?.depositBonusCode != null}
                      whiteTextDisabled
                      successBorder={user?.depositBonusCode != null}
                      animatedPlaceholder={t('deposits:bonus.placeholder')}
                    />
                  </div>
                  <div className="form-group__button">
                    {user?.depositBonusCode != null ? (
                      <Button
                        green
                        xLarge
                        wide
                        onClick={handleRemoveCode}
                        pending={bonusCodeRemovalState.pending}
                      >
                        {t('glossary:remove')}
                      </Button>
                    ) : (
                      <Button
                        grayGradient
                        xLarge
                        wide
                        type="submit"
                        pending={bonusCodeState.pending}
                      >
                        {t('glossary:add')}
                      </Button>
                    )}
                  </div>
                </div>
                {activeOffer}
              </FormGroup>
            </Form>
          )}
        </>
      )}
    </ScInputPromo>
  );
};

const Analytics = () => {
  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);
  const { sendEventToAnalytics } = useAnalytics();

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
